// @givmo/org-console — the Profile page's QR code card (PE-WEB11; plan §FC-14 "The Profile-page
// placement").
//
// On the View-as-public tab, directly under the public-page link or statement: the organization's
// PUBLIC QR code in its SAVED default design, its three downloads, and "Customize", which opens the
// shared `OrgQrDesignEditor` on that code. The same QR row as the Fundraisers share panel, so the two
// placements read as one feature. What the code leads to — and so the card's one line — is the host's.
//
// Rendering the public code takes `CAPABILITY_MANAGE_ORG_QR` (who holds it is the backend's data), so
// without it there is NO card: never a card whose image the server would refuse. A route miss is the
// code being absent here (the design switched off, or the public target dark), and it too draws
// nothing; the one refusal a person can act on — the organization not yet in standing for its public
// target — is said in the host's words.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { firstErrorCode, isGivmoError } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  ErrorMessage,
  Icon,
  Skeleton,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { Download, Palette, QrCode } from 'lucide-react';
import {
  ORG_QR_FORMATS,
  downloadPublicQr,
  orgPublicQrOptions,
  type OrgQrFormat,
} from './org-qr-design-queries';
import { CAPABILITY_MANAGE_ORG_QR, holdsCapability } from './standing-read';

export interface OrgPublicQrCardProps {
  orgRef: string;
  /** The standing read's capabilities; `null` while not reported draws nothing. */
  capabilities: readonly string[] | null;
  /** Where a scan lands, in the host's words. */
  line: string;
  /** The public code's refusal while the organization is not yet in standing, in the host's words. */
  targetUnavailable: string;
  /** Opens the editor on the public code. */
  onCustomize: () => void;
  testIdPrefix: string;
}

export function OrgPublicQrCard({
  orgRef,
  capabilities,
  line,
  targetUnavailable,
  onCustomize,
  testIdPrefix,
}: OrgPublicQrCardProps) {
  const prefix = `${testIdPrefix}-public-qr`;
  const permitted = holdsCapability(capabilities, CAPABILITY_MANAGE_ORG_QR);
  const preview = useQuery({
    ...orgPublicQrOptions(orgRef),
    enabled: permitted && orgRef.length > 0,
  });
  const [downloading, setDownloading] = useState<OrgQrFormat | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  if (!permitted) return null;
  const failure = isGivmoError(preview.error) ? preview.error : null;
  if (failure?.status === 404) return null;
  const refused =
    failure !== null &&
    failure.status === 409 &&
    firstErrorCode(failure) === 'ORG_SHARE_TARGET_UNAVAILABLE';

  async function download(format: OrgQrFormat) {
    setDownloading(format);
    setDownloadError(null);
    try {
      await downloadPublicQr(orgRef, format);
      toast.success('QR code downloaded');
    } catch {
      setDownloadError('Couldn’t download the QR code. Please try again.');
    } finally {
      setDownloading(null);
    }
  }

  return (
    <Card testId={prefix}>
      {/* Phone: the code beside its words, the downloads on one row beneath both. Wider: the
          downloads sit under the words, the code spanning both rows. */}
      <CardBody className="grid grid-cols-[96px_minmax(0,1fr)] gap-x-16 gap-y-16 sm:grid-cols-[120px_minmax(0,1fr)] sm:gap-x-20 sm:gap-y-12">
        <div className="sm:row-span-2">
          {preview.isSuccess ? (
            <img
              src={preview.data}
              alt="Your QR code"
              className="block h-auto w-full"
              {...testIdProp(`${prefix}-image`)}
            />
          ) : preview.isError ? (
            <span
              aria-hidden
              className="flex aspect-square w-full items-center justify-center rounded-md border border-dashed border-border text-icon-muted"
            >
              <Icon icon={QrCode} size={24} />
            </span>
          ) : (
            <Skeleton className="aspect-square w-full" />
          )}
        </div>
        <div className="flex min-w-0 flex-col gap-8">
          <div className="flex items-center gap-8">
            <Icon icon={QrCode} size={16} className="text-text-secondary" aria-hidden />
            <span className="text-s14 font-w600 text-text-strong">QR code</span>
            {!refused && (
              <Button
                appearance="saas"
                tone="link"
                size="sm"
                className="ml-auto max-lg:min-h-44"
                leadingIcon={<Icon icon={Palette} size={14} aria-hidden />}
                onClick={onCustomize}
                testId={`${prefix}-customize`}
              >
                Customize
              </Button>
            )}
          </div>
          <p className="text-s13 text-text-secondary" {...testIdProp(`${prefix}-line`)}>
            {refused ? targetUnavailable : line}
          </p>
          {preview.isError && !refused && (
            <div className="flex flex-wrap items-center gap-8">
              <p className="text-s13 text-text-secondary" {...testIdProp(`${prefix}-error`)}>
                Couldn’t load your QR code.
              </p>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                className="max-lg:min-h-44"
                onClick={() => void preview.refetch()}
                testId={`${prefix}-retry`}
              >
                Try again
              </Button>
            </div>
          )}
        </div>
        {!refused && (
          <div className="col-span-2 flex flex-col justify-center gap-8 sm:col-span-1 sm:col-start-2">
            <div className="flex flex-wrap items-center gap-8">
              {ORG_QR_FORMATS.map((format) => (
                <Button
                  key={format}
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  leadingIcon={<Icon icon={Download} size={14} aria-hidden />}
                  className="max-lg:min-h-44"
                  disabled={!preview.isSuccess || (downloading !== null && downloading !== format)}
                  loading={downloading === format}
                  onClick={() => void download(format)}
                  testId={`${prefix}-${format}`}
                >
                  {format.toUpperCase()}
                </Button>
              ))}
            </div>
            {downloadError !== null && (
              <ErrorMessage testId={`${prefix}-download-error`}>{downloadError}</ErrorMessage>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
